/**
 * TableAnalytics：表格列配置构建器（扁平表 & 交叉表）。
 *
 * 本文件主要把上层“字段/维度/指标”抽象成 AliTableAnalytics 所需的列结构，并在构建阶段叠加业务规则：
 * - 根据用户字段设置（`fieldSetting`）覆盖 leaf 列的 `cellType`
 * - 根据条件格式（`conditionalFormat`）覆盖 leaf 列的柱状百分比展示
 * - 对比列（对比日期 + 指标列）统一使用 `Compare` cellType，保证渲染一致
 * - 交叉表组表头支持根据 `freezeCount` 进行锁定（lock）
 */

import { isEmpty } from 'lodash-es';
import React from 'react';
import {
  generateTableColumns,
  setColumnConditionalFormatting,
  setColumnWidth,
} from '@customComp/AliTableAnalytics/utils';
import { findNumericalConfigForColumn } from '@customComp/AliTableAnalytics/utils/applyColumnConditionalFormat';
import type { INumericalFormatRule } from '@dbl/core/chartEditor';
import {
  AliTableCellTypeEnum,
  type ColumnDataType,
  type ITableColumnInfo,
  type TBarPercentConfig,
  type TAliTableColumn,
} from '@customComp/AliTableAnalytics/types';
import { getLanguage } from '@home/utils/common';

export type TCrosstabLeafColumn = {
  name?: string; // 中文名
  name_cn?: string;
  name_en?: string;
  code: string; // 如 ct_0
  origin_name: string; // 字段原始名
  type: 0 | 1; // 0 指标列, 1 维度列
  column_date_type: ColumnDataType;
  aggregate?: string;
  accuracy?: number;
  unit?: string | null;
  table_code?: string;
  can_sort?: boolean;
  is_compare_date?: boolean;
  compare_name_en?: string;
  compare_name_cn?: string;
  is_user_filter?: boolean;
};

export type TCrosstabGroupColumn = {
  name: string;
  name_en?: string;
  children?: TCrosstabColumnNode[];
  code?: string;
  groupCode?: string;
  table_code?: string;
  origin_name?: string;
  type?: 0 | 1;
  is_compare_date?: boolean;
  compare_name_en?: string;
  compare_name_cn?: string;
};

export type TCrosstabColumnNode = TCrosstabLeafColumn | TCrosstabGroupColumn;

type TFlatTableColumnsParams = {
  columnName: ITableColumnInfo[];
  isEn: boolean;
  sortable?: boolean;
  freezeCount: number;
  tableColumnWidths?: number[];
  fieldSetting?: Record<string, { format?: AliTableCellTypeEnum }>;
  conditionalFormat?: unknown;
};

type TCrosstabTableColumnsParams = {
  columnName: ITableColumnInfo[];
  crosstabColumns: TCrosstabColumnNode[];
  isEn: boolean;
  sortable?: boolean;
  freezeCount: number;
  tableColumnWidths?: number[];
  fieldSetting?: Record<string, { format?: AliTableCellTypeEnum }>;
  conditionalFormat?: unknown;
};

const buildCompareHeaderTitle = (baseText: string, contrastTextRaw: string | undefined): React.ReactNode => {
  const contrastText = String(contrastTextRaw ?? '').trim();
  if (!contrastText) return undefined;

  return React.createElement(
    'span',
    { className: 'dbl-compare-header' },
    React.createElement('span', { className: 'dbl-compare-header__base' }, baseText),
    React.createElement('span', { className: 'dbl-compare-header__contrast' }, contrastText),
  );
};

/**
 * 构建扁平表列（无分组树）。
 *
 * 设计要点/约束：
 * - `fieldSetting`、`conditionalFormat` 的作用范围是“最终列集合”（扁平表场景无需区分 leaf/group）。
 * - 当存在对比日期列时，需要把“对比日期列 + 所有指标列(type=0)”都标记为 Compare cell，
 *   以保证对比渲染在同一行/同一表头下不会出现类型不一致的问题。
 *
 * @param params 输入参数
 * @returns columns：AliTableAnalytics 列数组；leafCodes：返回每一列的 code（扁平表无树结构时即为列全集）
 */
export const buildFlatTableColumns = ({
  columnName,
  isEn,
  sortable,
  freezeCount,
  tableColumnWidths,
  fieldSetting,
  conditionalFormat,
}: TFlatTableColumnsParams): { columns: TAliTableColumn[]; leafCodes: string[] } => {
  const columnInfos: ITableColumnInfo[] = columnName;

  // ===== 1) 基础列生成：由 columns 信息生成 AliTableColumn 列表 =====
  let nextColumns: TAliTableColumn[] = generateTableColumns(columnInfos, {
    isEn,
    sortable: !!sortable,
    freezeCount,
  });

  // ===== 2) 宽度覆盖：仅在上层显式传入 width 配置时生效 =====
  if (Array.isArray(tableColumnWidths) && tableColumnWidths.length > 0) {
    nextColumns = setColumnWidth(nextColumns, tableColumnWidths);
  }

  // ===== 3) 单元格格式：根据 table_code+origin_name 定位配置项 =====
  // 约定：当后端/配置缺少 `table_code` 时，使用默认 `t0` 作为兜底 key，
  // 以确保字段设置在缺省场景下仍可找到对应格式。
  columnInfos.forEach((column: ITableColumnInfo, index: number) => {
    const configKey = `${column.table_code || 't0'}_${column.origin_name}`;
    const config = fieldSetting?.[configKey] || {};
    nextColumns[index].cellType = config.format ?? AliTableCellTypeEnum.Original;
  });

  // ===== 4) 条件格式：仅在 conditionalFormat 非空时叠加 =====
  if (!isEmpty(conditionalFormat)) {
    nextColumns = setColumnConditionalFormatting(nextColumns, conditionalFormat);
  }

  // ===== 5) Compare：存在对比日期列时，统一对比单元格的 cellType =====
  // 业务契约：对比日期列 + 所有指标列(type=0) 都要标记为 Compare，
  // 避免同一张表中对比行/非对比行的 cellType 产生渲染差异。
  const isCompare = columnInfos.some((column: ITableColumnInfo) => column?.is_compare_date === true);
  if (isCompare) {
    nextColumns = nextColumns.map((column) => {
      const { type, isCompareDate } = column;
      const isMetric = type === 0;
      const isCompareCell = isCompareDate || isMetric;
      return {
        ...column,
        isCompareCell,
        cellType: isCompareCell ? AliTableCellTypeEnum.Compare : column.cellType,
      };
    });
  }

  return { columns: nextColumns, leafCodes: nextColumns.map((c) => c.code) };
};

/**
 * 构建交叉表列（带分组表头树）。
 *
 * 设计要点/约束：
 * - `crosstabColumns` 可能包含 group 节点和 leaf 节点；最终 AliTable 列结构需要的是“列树 + leaf 叶子列”。
 * - `fieldSetting`、`conditionalFormat` 以及条形百分比配置只作用于 leaf 列（group 只是表头容器）。
 * - 对比列同样只需要在 leaf 列上设置 `cellType=Compare`，以保证渲染命中列数据单元格。
 * - 分组表头锁定：根据 `freezeCount` 决定前 N 个分组是否 lock；只锁分组，不锁 leaf。
 *
 * @param params 输入参数
 * @returns columns：带分组树的 AliTable 列数组；leafCodes：叶子列 code 列表（用于后续对齐/导出）
 */
export const buildCrosstabTableColumns = ({
  columnName,
  crosstabColumns,
  isEn,
  sortable,
  freezeCount,
  tableColumnWidths,
  fieldSetting,
  conditionalFormat,
}: TCrosstabTableColumnsParams): { columns: TAliTableColumn[]; leafCodes: string[] } => {
  // ===== 1) 先把树形 crosstab node flatten 成 leafInfos（供 generateTableColumns 使用） =====
  const collectLeafInfos = (nodes: TCrosstabColumnNode[]): ITableColumnInfo[] => {
    const leafInfos: ITableColumnInfo[] = [];

    nodes.forEach((node) => {
      const maybeChildren = (node as TCrosstabGroupColumn).children;
      const hasChildren = Array.isArray(maybeChildren) && maybeChildren.length > 0;

      // group 节点：递归收集其子节点的 leaf 信息
      if (hasChildren) {
        leafInfos.push(...collectLeafInfos(maybeChildren as TCrosstabColumnNode[]));
        return;
      }

      // leaf 节点：转成 ITableColumnInfo。注意这里的 `name` 需要使用 leaf.code，
      // 因为 generateTableColumns 内部会把 name 当作 code 使用（否则无法与后续 leaf code 对齐）。
      const leaf = node as TCrosstabLeafColumn;
      leafInfos.push({
        // 透传叶子列 code：generateTableColumns 内部会把 name 当作 code 使用
        // 叶子表头显示仍由 name_cn / name_en 决定（不会把 code 当作标题）。
        name: leaf.code,
        name_cn: leaf.name || leaf.name_en,
        name_en: leaf.name_en || leaf.name,
        origin_name: leaf.origin_name,
        can_sort: leaf.can_sort,
        type: leaf.type,
        column_date_type: leaf.column_date_type,
        aggregate: leaf.aggregate,
        accuracy: leaf.accuracy ?? 0,
        unit: leaf.unit ?? null,
        table_code: leaf.table_code,
        is_compare_date: leaf.is_compare_date,
        is_user_filter: leaf.is_user_filter,
      });
    });

    return leafInfos;
  };

  const leafInfos = collectLeafInfos(crosstabColumns);
  const leafColumns = generateTableColumns(leafInfos, {
    isEn,
    sortable: !!sortable,
    // 交叉表冻结语义统一按“最终展示的 leaf 列顺序”处理，保证从左到右冻结。
    freezeCount,
  });

  // ===== 2) 交叉表 leaf 宽度：只按 leaf 顺序做映射 =====
  // 约定：上层 width 数组只关心 leaf 的展示顺序，不包含 group 表头的宽度。
  // 叶子列的宽度：仅映射到 leaf 顺序
  if (Array.isArray(tableColumnWidths) && tableColumnWidths.length > 0) {
    leafColumns.forEach((column, index) => {
      column.width = tableColumnWidths[index] ?? column.width ?? 180;
    });
  }

  // 用于后续把配置项精确映射到 leaf 列
  const leafInfoMap = new Map<string, ITableColumnInfo>(leafInfos.map((info) => [info.name, info]));
  const leafColumnMap = new Map<string, TAliTableColumn>(leafColumns.map((c) => [c.code, c]));

  // ===== 3) fieldSetting：只作用于 leaf =====
  if (!isEmpty(fieldSetting)) {
    leafColumns.forEach((column) => {
      const info = leafInfoMap.get(column.code);
      if (!info) return;

      // 兜底 key 与 buildFlatTableColumns 保持一致：缺省 table_code 视作 t0
      const configKey = `${info.table_code || 't0'}_${info.origin_name}`;
      const config = fieldSetting?.[configKey] || {};
      column.cellType = config.format ?? AliTableCellTypeEnum.Original;
    });
  }

  // ===== 4) conditionalFormat：只作用于 leaf =====
  if (!isEmpty(conditionalFormat)) {
    // 用 crosstabColumns 判断设置条件格式：
    // 任一层级（当前节点或其父级）命中 table_code + origin_name 时，都对对应叶子列生效。
    const barPercentConfig = (
      conditionalFormat as { table_bar_percentage?: TBarPercentConfig[] }
    )?.table_bar_percentage;

    if (Array.isArray(barPercentConfig) && barPercentConfig.length > 0) {
      const findBarItem = (
        tableCode?: string,
        originName?: string,
      ): TBarPercentConfig | undefined => {
        if (!originName) return undefined;
        const normalizedTableCode = tableCode || 't0';
        return barPercentConfig.find(
          (item) =>
            (item?.table_code || 't0') === normalizedTableCode &&
            item?.metric === originName,
        );
      };

      const leafBarItemMap = new Map<string, TBarPercentConfig>();
      const collectLeafBarConfig = (
        nodes: TCrosstabColumnNode[],
        parentBarItem?: TBarPercentConfig,
      ): void => {
        nodes.forEach((node) => {
          const groupNode = node as TCrosstabGroupColumn;
          const maybeChildren = groupNode.children;
          const hasChildren = Array.isArray(maybeChildren) && maybeChildren.length > 0;
          const selfBarItem = findBarItem(groupNode.table_code, groupNode.origin_name);
          const activeBarItem = selfBarItem || parentBarItem;

          if (hasChildren) {
            collectLeafBarConfig(
              maybeChildren as TCrosstabColumnNode[],
              activeBarItem,
            );
            return;
          }

          const leaf = node as TCrosstabLeafColumn;
          const leafBarItem = findBarItem(leaf.table_code, leaf.origin_name) || activeBarItem;
          if (leaf?.code && leafBarItem) {
            leafBarItemMap.set(leaf.code, leafBarItem);
          }
        });
      };

      collectLeafBarConfig(crosstabColumns);

      leafColumns.forEach((column) => {
        // 用leafBarItemMap和findBarItem获取条件格式配置
        // 如果最底层没有需要向父级匹配查询，如果符合则设置条件格式展示
        const barItem =
          leafBarItemMap.get(column.code) ||
          findBarItem(column?.tableCode, column?.originName);

        if (barItem) {
          column.cellType = AliTableCellTypeEnum.BarPercent;
          column.barPercentConfig = barItem;
        }
      });
    }

    const numericalList = (
      conditionalFormat as {
        table_numerical_values?: Array<{
          metric?: string;
          id?: string;
          table_code?: string;
          rules?: INumericalFormatRule[];
        }>;
      }
    )?.table_numerical_values;

    if (Array.isArray(numericalList) && numericalList.length > 0) {
      leafColumns.forEach(column => {
        const numericalItem = findNumericalConfigForColumn(
          numericalList,
          column.tableCode,
          column.originName,
        );
        if (!numericalItem?.rules?.length) {
          return;
        }
        column.numericalFormatConfig = { rules: numericalItem.rules };
        if (column.barPercentConfig) {
          column.cellType = AliTableCellTypeEnum.BarPercentNumerical;
        } else {
          column.cellType = AliTableCellTypeEnum.NumericalCondition;
        }
      });
    }
  }

  // ===== 5) Compare：只作用于 leaf（对比日期列 + 指标列(type=0)） =====
  const isCompare = columnName?.some((column: ITableColumnInfo) => column?.is_compare_date === true);
  if (isCompare) {
    leafColumns.forEach((column) => {
      const info = leafInfoMap.get(column.code);
      if (!info) return;

      // 指标列没有 is_compare_date 字段，但依旧需要纳入对比渲染集合
      const isMetric = info.type === 0;
      const isCompareCell = Boolean(info.is_compare_date) || isMetric;
      column.isCompareCell = isCompareCell;
      // 如果是指标，需要设置isCompareDate为false，渲染成对比指标列的格式
      if (isMetric) {
        column.isCompareDate = false;
      }
      if (isCompareCell) {
        column.cellType = AliTableCellTypeEnum.Compare;
      }
    });
  }

  // ===== 6) 分组表头：group 的 lock 由其子列锁定态自动推导 =====
  // 说明：
  // - leaf 已按左到右顺序基于 freezeCount 设置 lock。
  // - group 只作为容器，是否锁定取决于其 children 中是否存在被锁定列。
  let groupCodeIndex = 0;
  const buildColumns = (nodes: TCrosstabColumnNode[]): TAliTableColumn[] => {
    return nodes.map((node) => {
      const maybeChildren = (node as TCrosstabGroupColumn).children;
      const hasChildren = Array.isArray(maybeChildren) && maybeChildren.length > 0;

      if (!hasChildren) {
        // leaf 节点：复用已构建好的 leafColumns；如该节点自身 is_compare_date=true，则在叶子表头层级渲染两行对比标题
        const leaf = node as TCrosstabLeafColumn;
        const column = leafColumnMap.get(leaf.code) as TAliTableColumn;
        if (!column) return column;

        if (leaf.is_compare_date === true) {
          const baseText = String(column.name ?? '');
          const contrastTextRaw = getLanguage(isEn, leaf.compare_name_en, leaf.compare_name_cn);
          const title = buildCompareHeaderTitle(baseText, contrastTextRaw);
          if (title) return { ...column, title } as TAliTableColumn;
        }

        return column;
      }

      // group 节点：构建表头容器，并把 children columns 挂在其下
      const groupNode = node as TCrosstabGroupColumn;
      const childrenColumns = buildColumns(maybeChildren as TCrosstabColumnNode[]);

      const firstChildColumn = childrenColumns?.[0];
      const fallbackDescKey = firstChildColumn?.descKey;

      // descKey 作为表头描述/排序的稳定标识：
      // - 优先使用 groupNode 的 table_code + origin_name + type 组合生成
      // - 当缺少必要字段时退回到 child 的 descKey，避免出现空 descKey 导致的 UI/导出异常
      const computedDescKey =
        groupNode.table_code && groupNode.origin_name && typeof groupNode.type === 'number'
          ? `${groupNode.table_code || 't0'}_${groupNode.origin_name}_${groupNode.type}`
          : fallbackDescKey;

      const title = getLanguage(isEn, groupNode.name_en, groupNode.name);
      const lock = childrenColumns.some((column) => Boolean(column?.lock));
      const currentGroupCodeIndex = groupCodeIndex;
      groupCodeIndex += 1;

      const base = firstChildColumn as TAliTableColumn;
      const groupColumn: TAliTableColumn = {
        // ArtColumn 字段
        code: groupNode.code || `group_${currentGroupCodeIndex}`,
        name: title,
        children: childrenColumns,
        // 交叉表分组表头统一居中，只有最底层 leaf 列保留原始对齐方式。
        align: 'center',
        features: { sortable: true },
        lock,
        // TCustomColumn 字段
        isEn,
        valueType: base?.valueType as ColumnDataType,
        cellType: AliTableCellTypeEnum.Original,
        descKey: computedDescKey || fallbackDescKey || '',
        barPercentConfig: undefined,
        originName: base?.originName,
        tableCode: base?.tableCode,
        accuracy: base?.accuracy ?? 0,
        unit: base?.unit ?? '',
        type: base?.type ?? 0,
      };

      // group 节点：只依赖 groupNode 自身 is_compare_date，不做跨层继承
      if (groupNode.is_compare_date === true) {
        const contrastTextRaw = getLanguage(isEn, groupNode.compare_name_en, groupNode.compare_name_cn);
        const groupTitle = buildCompareHeaderTitle(String(groupColumn.name ?? ''), contrastTextRaw);
        if (groupTitle) groupColumn.title = groupTitle;
      }

      return groupColumn;
    });
  };

  const columnsTree = buildColumns(crosstabColumns);

  return { columns: columnsTree, leafCodes: leafColumns.map((c) => c.code) };
};

